import {Graphics} from "pixi.js";
import {BlockItemDrawProps} from "../FrameBlockItem";
import {lineBlueGrey, lineBlueHeavy, panelMetalGrey} from "../../../const";
import {ClickableFace, drawFace} from "./ClickableFace";
import {SlotLabel} from "./SlotLabel";
import {slotOfCell} from "../../../model/frame/MdFrameSpec";
import {swingSideFor} from "../../frameProfile/Helpers";
import type {SlotDoor} from "../../../ElevationApp";
import {LeafRect, SwingPreview} from "./DoorFace";

// DoorSlotFace is a secondary door slot of a multi-opening screen: the door of the opening that
// fills it, drawn from that opening's own door elevation (leaves and lites), with a
// "slot - opening" label and the hover swing preview. Read-only here: the hinges, hardware and lites
// are edited on that opening. An empty slot draws its outline only.
export class DoorSlotFace extends ClickableFace {
    // held in a box so the fill closure (built before `this` exists) and destroy() share them.
    private labelBox: {label: SlotLabel; swing: SwingPreview};

    constructor() {
        const labelBox = {label: new SlotLabel(), swing: new SwingPreview()};
        super((props: BlockItemDrawProps) => {
            const g = props.graphics;
            g.clear();

            const left = Math.min(props.startX, props.endX);
            const right = Math.max(props.startX, props.endX);
            const top = Math.min(props.startY, props.endY);
            const bottom = Math.max(props.startY, props.endY);
            if(right - left <= 0 || bottom - top <= 0) return;

            const slot = slotOfCell(props.elev.frameSpec, props.block.id);
            const opening = slot ? props.elev.slotOpening(slot.name) : null;
            const door = slot ? props.elev.slotDoors.get(slot.name) : undefined;

            if(door && opening) {
                labelBox.swing.update(props, drawSlotDoor(g, props, door, left, top, right, bottom));
            } else {
                labelBox.swing.update(props, []);
                g.lineStyle(2, lineBlueHeavy, 1);
                g.beginFill(0xffffff, 0.6);
                drawFace(g, props, left, top, right - left, bottom - top);
                g.endFill();
            }

            labelBox.label.draw(props, slot ? `${slot.name} - ${opening?.name ?? "no opening"}` : null);
        });

        this.labelBox = labelBox;
    }

    destroy() {
        this.labelBox.label.destroy();
        this.labelBox.swing.destroy();
        super.destroy();
    }
}

// the slot's door: one leaf, or a pair split at its own active width and pair gap. Mirrors
// DoorFace's leaf geometry, from the slot door's own specs. Returns the drawn leaves.
function drawSlotDoor(g: Graphics, props: BlockItemDrawProps, slot: SlotDoor, left: number, top: number, right: number, bottom: number): LeafRect[] {
    const frame = slot.frame;

    if(!frame.isPair) {
        return [drawSlotLeaf(g, props, slot, true, left, top, right, bottom, null)].filter(l => !!l) as LeafRect[];
    }

    const width = right - left;
    const activeWidth = frame.activeDoorWidth.get();
    const activeIsOnLeft = swingSideFor(frame, true).side === "left";
    const gap = frame.pairDoorGap.get();

    const activeEdge = activeIsOnLeft ? activeWidth : width - activeWidth;
    const gapLeft = left + (activeIsOnLeft ? activeEdge : activeEdge - gap);
    const gapRight = left + (activeIsOnLeft ? activeEdge + gap : activeEdge);

    return [
        drawSlotLeaf(g, props, slot, activeIsOnLeft, left, top, gapLeft, bottom, "right"),
        drawSlotLeaf(g, props, slot, !activeIsOnLeft, gapRight, top, right, bottom, "left"),
    ].filter(l => !!l) as LeafRect[];
}

// one leaf inset by the slot door's gaps and cuts, with its glass lites. The frame undercut is the
// primary's: the whole frame is cut by it (see DoorFace.renderDoor). Returns the drawn leaf, or null
// when it has no size.
function drawSlotLeaf(g: Graphics, props: BlockItemDrawProps, slot: SlotDoor, active: boolean,
                      leftX: number, topY: number, rightX: number, bottomY: number, meetingSide: "left" | "right" | null): LeafRect | null {
    const frame = slot.frame;
    const door = slot.door;

    const swing = swingSideFor(frame, active);
    const hingesOnLeft = swing.side === "left";
    const jambGap = frame.frameJambToDoorGap.get();
    const stopGap = frame.frameStopToDoorGap.get();

    const leftInset = meetingSide === "left" ? 0 : (hingesOnLeft ? jambGap : stopGap);
    const rightInset = meetingSide === "right" ? 0 : (hingesOnLeft ? stopGap : jambGap);

    const doorLeft = leftX + leftInset;
    const doorRight = rightX - rightInset;
    const netUndercut = Math.max(0, door.undercut.get() - props.elev.frameSpec.undercutFrame.get());
    const doorTop = topY + door.topGap.get() + door.overcut.get();
    const doorBottom = bottomY - door.bottomGap.get() - netUndercut;

    const leafWidth = doorRight - doorLeft;
    const leafHeight = doorBottom - doorTop;
    if(leafWidth <= 0 || leafHeight <= 0) return null;

    g.lineStyle(2, lineBlueHeavy, 1);
    g.beginFill(panelMetalGrey, 1);
    g.drawRect(doorLeft, doorTop, leafWidth, leafHeight);
    g.endFill();

    g.lineStyle(1, lineBlueHeavy, 1);
    const lites = door.litesFor(active ? "active" : "inactive").map(lite => {
        lite.normalize(leafWidth, leafHeight);
        const liteLeft = doorLeft + lite.offsetLeft.get();
        const liteTop = doorTop + lite.offsetTop.get();
        g.beginFill(lineBlueGrey, 1);
        g.drawRect(liteLeft, liteTop, lite.width.get(), lite.height.get());
        g.endFill();
        return {left: liteLeft, top: liteTop, right: liteLeft + lite.width.get(), bottom: liteTop + lite.height.get()};
    });

    return {doorLeft, doorRight, doorTop, doorBottom, cut: netUndercut, hingesOnLeft, swingsOut: swing.direction !== "in", lites};
}
